import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { vendorsAPI } from '../../services/api';
import toast from 'react-hot-toast';
import { Pencil, Trash2 } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import PrintButton from '../../utils/print';
import PrintLetterhead from '../common/PrintLetterhead';
import FxAmount from '../common/FxAmount';
import useExchangeRates from '../common/useExchangeRates';
import { useCompany } from '../common/useCompany';

export default function VendorList() {
  const { hasPermission } = useAuth();
  const navigate = useNavigate();
  const company = useCompany();
  const base = company?.base_currency || 'USD';
  const { getRate } = useExchangeRates();
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const listRef = useRef(null);

  const load = (q = search) => {
    vendorsAPI.list({ search: q }).then(setVendors).catch(e => toast.error(e.message)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [search]);

  const doDelete = async (e, v) => {
    e.stopPropagation();
    if (!window.confirm(`Delete vendor "${v.name}"?`)) return;
    try {
      await vendorsAPI.remove(v.id);
      toast.success('Vendor deleted');
      load();
    } catch (err) { toast.error(err.message); }
  };

  return (
    <div>
      <div className="toolbar">
        <h2>Vendors</h2>
        <div className="toolbar-right">
          <input className="form-control" placeholder="Search vendors..." value={search} onChange={e => setSearch(e.target.value)} style={{width:250}} />
          <PrintButton targetRef={listRef} title={`Vendors - ${new Date().toLocaleDateString()}`} />
          {hasPermission('vendors:create') && <button className="btn btn-primary" onClick={() => navigate('/vendors/new')}>+ New Vendor</button>}
        </div>
      </div>
      {loading ? <div className="spinner" style={{margin:'40px auto'}} /> : (
        <div className="card" ref={listRef}><div className="card-body" style={{padding:0}}>
          <PrintLetterhead />
          <table>
            <thead><tr><th>#</th><th>Name</th><th>Email</th><th>Phone</th><th className="text-right">Balance ({base})</th><th>Status</th><th style={{width:80}}></th></tr></thead>
            <tbody>
              {vendors.map(v => (
                <tr key={v.id} onClick={() => navigate(`/vendors/${v.id}`)} style={{cursor:'pointer'}}>
                  <td className="font-mono">{v.vendor_number}</td>
                  <td><strong>{v.name}</strong></td>
                  <td className="text-muted">{v.email || '-'}</td>
                  <td>{v.phone || '-'}</td>
                  <td className={`amount ${parseFloat(v.balance) > 0 ? 'negative' : ''}`}>
                    {/* Balances live in the company base currency; the sub-line
                        restates them in the vendor's own currency. */}
                    <FxAmount
                      amount={v.balance}
                      currency={base}
                      baseCurrency={base}
                      convertTo={v.currency}
                      rate={getRate(v.currency, base)}
                      style={{ textAlign: 'right' }}
                    />
                  </td>
                  <td>{v.is_active ? <span className="badge badge-posted">Active</span> : <span className="badge badge-voided">Inactive</span>}</td>
                  <td style={{whiteSpace:'nowrap'}}>
                    <button className="btn btn-ghost btn-sm" title="Edit" onClick={(e)=>{ e.stopPropagation(); navigate(`/vendors/${v.id}`); }}><Pencil size={13} /></button>
                    <button className="btn btn-ghost btn-sm" title="Delete" onClick={(e)=>doDelete(e, v)}><Trash2 size={13} /></button>
                  </td>
                </tr>
              ))}
              {vendors.length === 0 && <tr><td colSpan={7} className="text-center text-muted" style={{padding:30}}>No vendors found</td></tr>}
            </tbody>
          </table>
        </div></div>
      )}
    </div>
  );
}
